import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormGroupDirective, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { U2bValidators } from '@shared/validators/validators';
import { startOfToday } from '@core/date.facade';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { TolgeeService } from '@core/translation/tolgee.service';

@Component({
    selector: 'form-widget-date-range',
    templateUrl: './form-widget-date-range.component.html',
    styles: [`
        :host [matsuffix][disabled] {
            display: none;
        }
    `],
    standalone: false
})
export class FormWidgetDateRangeComponent implements OnInit, OnChanges {

    @Input()
    headline: string;

    @Input()
    headlineKey = 'common.dateRange.choosePeriodHeadline';

    @Input()
    parentFormGroup: UntypedFormGroup;

    @Input()
    hideHourMinFields = false;

    @Input()
    from: Date;

    /** When set, overrides {@link fromLabelKey} (e.g. pre-translated or custom label). */
    @Input()
    fromLabel: string;

    @Input()
    fromLabelKey = 'common.dateRange.from';

    @Input()
    to: Date;

    @Input()
    toLabel: string;

    @Input()
    toLabelKey = 'common.dateRange.to';

    @Input()
    appearance: MatFormFieldAppearance = 'outline';

    @Input()
    showFromField = true;

    @Input()
    showToField = true;

    @Input()
    fromRequired = true;

    @Input()
    toRequired = false;

    dateRageFormGroup: UntypedFormGroup;

    today = startOfToday();

    constructor(private _formBuilder: UntypedFormBuilder,
                private _formGroupDirective: FormGroupDirective,
                private _tolgee: TolgeeService) {
    }

    ngOnInit(): void {
        this._createForm();
        if (this._formGroupDirective?.form && !this.parentFormGroup) {
            this.parentFormGroup = this._formGroupDirective.form;
        }
        setTimeout(() => {
            this.parentFormGroup.addControl('dateRangeForm', this.dateRageFormGroup);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.dateRageFormGroup && (this.from || this.to)) {
            this.dateRageFormGroup.patchValue({
                from: this.from,
                to: this.to
            });
        }
        if (this.dateRageFormGroup && (changes.fromLabel || changes.toLabel || changes.fromLabelKey || changes.toLabelKey)) {
            this.dateRageFormGroup.updateValueAndValidity({ emitEvent: false });
        }

        if (this.dateRageFormGroup && (changes.fromRequired || changes.toRequired || changes.showFromField || changes.showToField)) {
            this.syncFieldValidators();
        }
    }

    resetControl(control: AbstractControl): void {
        control?.reset();
    }

    resolveFromLabel(): string {
        return (this.fromLabel != null && this.fromLabel !== '')
            ? this.fromLabel
            : this._tolgee.translate(this.fromLabelKey);
    }

    resolveToLabel(): string {
        return (this.toLabel != null && this.toLabel !== '')
            ? this.toLabel
            : this._tolgee.translate(this.toLabelKey);
    }

    private _createForm(): void {
        const periodStartMsg = this._tolgee.translate('common.dateRange.periodStartRequired');
        this.dateRageFormGroup = this._formBuilder.group({
                from: [this.from || this.today, []],
                to: [this.to || null],
            },
            {
                validators: [
                    (formGroup: UntypedFormGroup) => {

                        const fromDate = formGroup.get('from').value;
                        const toDate = formGroup.get('to').value;

                        if (!fromDate || !toDate) {
                            return null;
                        }

                        const fromL = this.resolveFromLabel();
                        const toL = this.resolveToLabel();

                        if (fromDate > toDate) {
                            formGroup.get('from').setErrors({
                                wrongDateRange: {
                                    message: this._tolgee.translate('common.dateRange.wrongFromAfterTo', {
                                        fromLabel: fromL,
                                        toLabel: toL
                                    })
                                }
                            });
                            formGroup.get('to').setErrors({
                                wrongDateRange: {
                                    message: this._tolgee.translate('common.dateRange.wrongToBeforeFrom', {
                                        fromLabel: fromL,
                                        toLabel: toL
                                    })
                                }
                            });
                        } else {
                            formGroup.get('from').setErrors(null);
                            formGroup.get('to').setErrors(null);
                        }
                    }
                ],
            });
        this.syncFieldValidators();
    }

    private syncFieldValidators(): void {
        if (!this.dateRageFormGroup) {
            return;
        }

        const periodStartMsg = this._tolgee.translate('common.dateRange.periodStartRequired');
        const finishRequiredMsg = this._tolgee.translate('widgets.docksite.orderFormV2.planning.finishRequiredValidation');
        const fromControl = this.dateRageFormGroup.get('from');
        const toControl = this.dateRageFormGroup.get('to');

        fromControl?.setValidators(this.fromRequired && this.showFromField
            ? [U2bValidators.required(periodStartMsg)]
            : []);
        toControl?.setValidators(this.toRequired && this.showToField
            ? [U2bValidators.required(finishRequiredMsg)]
            : []);

        fromControl?.updateValueAndValidity({ emitEvent: false });
        toControl?.updateValueAndValidity({ emitEvent: false });
    }
}
